import { type ProviderInteractionMode, RuntimeMode } from "@cmmd-forge/contracts";
import { useCallback } from "react";

export function useComposerModeControls<ComposerTarget>(input: {
  readonly composerDraftTarget: ComposerTarget;
  readonly isLocalDraftThread: boolean;
  readonly runtimeMode: RuntimeMode;
  readonly interactionMode: ProviderInteractionMode;
  readonly setComposerDraftRuntimeMode: (
    target: ComposerTarget,
    runtimeMode: RuntimeMode | null | undefined,
  ) => void;
  readonly setComposerDraftInteractionMode: (
    target: ComposerTarget,
    interactionMode: ProviderInteractionMode | null | undefined,
  ) => void;
  readonly setDraftThreadContext: (
    target: ComposerTarget,
    context: {
      readonly runtimeMode?: RuntimeMode;
      readonly interactionMode?: ProviderInteractionMode;
    },
  ) => void;
  readonly scheduleComposerFocus: () => void;
}): {
  readonly handleRuntimeModeChange: (mode: RuntimeMode) => void;
  readonly handleInteractionModeChange: (mode: ProviderInteractionMode) => void;
  readonly toggleInteractionMode: () => void;
} {
  const handleRuntimeModeChange = useCallback(
    (mode: RuntimeMode) => {
      if (mode === input.runtimeMode) return;
      input.setComposerDraftRuntimeMode(input.composerDraftTarget, mode);
      if (input.isLocalDraftThread) {
        input.setDraftThreadContext(input.composerDraftTarget, { runtimeMode: mode });
      }
      input.scheduleComposerFocus();
    },
    [input],
  );

  const handleInteractionModeChange = useCallback(
    (mode: ProviderInteractionMode) => {
      if (mode === input.interactionMode) return;
      input.setComposerDraftInteractionMode(input.composerDraftTarget, mode);
      if (input.isLocalDraftThread) {
        input.setDraftThreadContext(input.composerDraftTarget, { interactionMode: mode });
      }
      input.scheduleComposerFocus();
    },
    [input],
  );

  const toggleInteractionMode = useCallback(() => {
    handleInteractionModeChange(input.interactionMode === "plan" ? "default" : "plan");
  }, [handleInteractionModeChange, input.interactionMode]);

  return { handleRuntimeModeChange, handleInteractionModeChange, toggleInteractionMode };
}
